import { zodResolver } from '@hookform/resolvers/zod'
import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDownIcon } from 'lucide-react'
import { use, useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod/v4'
import { ChartTimeframeProviderContext } from '@/components/shared/analytics/chart-timeframe/context/context'
import { DateRangeForm } from '@/components/shared/analytics/chart-timeframe/date-range-form'
import { LiveIndicator } from '@/components/shared/analytics/chart-timeframe/live-indicator'
import { Button } from '@/components/ui/button'
import { Form, FormField, FormItem } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select'
import {
  getIntervalInSeconds,
  getPresetTimeframe,
  getRefreshIntervalMs,
  getTimeframes,
} from '@/config/charts'
import { cn } from '@/lib/utils'

export const schema = z.object({
  key: z.string().nullish(),
  startDate: z.date(),
  endDate: z.date(),
})

const timeframes = getTimeframes()

export const ChartTimeframe = () => {
  const { timeframe, setTimeframe, isLive } = use(ChartTimeframeProviderContext)
  const [isRangePopoverOpen, setIsRangePopoverOpen] = useState(false)
  const isRangePopoverOpenRef = useRef(false)

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      key: timeframe?.key,
      startDate: timeframe?.startDate,
      endDate: timeframe?.endDate,
    },
  })

  useEffect(() => {
    if (isRangePopoverOpenRef.current) {
      return
    }
    form.setValue('key', timeframe.key ?? '')
    if (timeframe.startDate) {
      form.setValue('startDate', timeframe.startDate)
    }
    if (timeframe.endDate) {
      form.setValue('endDate', timeframe.endDate)
    }
  }, [timeframe, form])

  const handleRangePopoverOpenChange = useCallback(
    (isOpen: boolean) => {
      isRangePopoverOpenRef.current = isOpen
      setIsRangePopoverOpen(isOpen)
      if (isOpen) {
        form.reset({
          key: timeframe.key ?? '',
          startDate: timeframe.startDate,
          endDate: timeframe.endDate,
        })
      }
    },
    [form, timeframe]
  )

  const key = form.watch('key')

  const onSubmit = async (values: z.infer<typeof schema>) => {
    form.setValue('key', '')
    setTimeframe({
      startDate: values.startDate,
      endDate: values.endDate,
      intervalInSeconds: getIntervalInSeconds(values.startDate, values.endDate),
    })
  }

  return (
    <div className="flex items-center gap-4 py-1">
      {isLive ? (
        <LiveIndicator refreshIntervalMs={getRefreshIntervalMs(timeframe)} />
      ) : null}
      <Form {...form}>
        <form
          id="chart-timeframe-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-8"
        >
          <div className="flex items-center">
            <DateRangeForm
              isOpen={isRangePopoverOpen}
              onOpenChange={handleRangePopoverOpenChange}
            />
            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <Select
                    value={field?.value ?? ''}
                    onValueChange={(value) => {
                      const preset = getPresetTimeframe(value)
                      if (preset) {
                        setTimeframe(preset)
                      }
                      field.onChange(value)
                    }}
                  >
                    <SelectPrimitive.Trigger
                      data-slot="select-trigger"
                      data-size="default"
                    >
                      <Button
                        asChild
                        variant="outline"
                        className={cn(
                          'rounded-l-none border-l-0 p-2',
                          key ? 'w-60' : 'w-10.5'
                        )}
                      >
                        <div
                          className={cn(
                            'flex items-center',
                            key ? 'justify-between!' : 'justify-center'
                          )}
                        >
                          {key ? <SelectValue /> : null}
                          <ChevronDownIcon className="w-4 h-4" />
                        </div>
                      </Button>
                    </SelectPrimitive.Trigger>
                    <SelectContent
                      position="popper"
                      align="end"
                      className="rounded-xl"
                    >
                      {timeframes.map((tf) => (
                        <SelectItem
                          key={tf.key}
                          value={tf.key ?? ''}
                          className="rounded-lg"
                        >
                          {tf.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
    </div>
  )
}
